import React, { useState, useEffect } from "react";
import { Button, Input, Form, Select, message, Modal } from "antd";
import RelationFieldStandard from "../../qforms/standard_elements/RelationFieldStandard";
import PhotoButton from "../../qforms/standard_elements/PhotoButton";
import { useSingleItem } from "../../_actions/app/items";
import { useGenImage } from "../../_actions/bot";
import { getImageURL } from "../../_helpers/image";

const { Option } = Select;
const { TextArea } = Input;

export default function ImageGenerator() {
    const [form] = Form.useForm();
    const [imageStyleInputs, setImageStyleInputs] = useState({});
    const [selectedStyleId, setSelectedStyleId] = useState(null);
    const [modelOptions, setModelOptions] = useState([]);
    const [generatedImageId, setGeneratedImageId] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const mutationImageGen = useGenImage(
        (result) => {
            console.log(result, "Image gen Result");
            if (result?.data?.id) {
                const imageId = result?.data?.id;
                setGeneratedImageId(imageId);
                setIsModalVisible(true);
                message.success("Image generated successfully!");
            }
        },
        (error) => {
            console.log(error, "image gen failed");
            const errorMessage =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to generate image. Please try again.";
            message.error(errorMessage);
        }
    );

    const {
        data: dataItem,
        isLoading: loadingItem,
        isSuccess: successItem,
    } = useSingleItem(
        "qdb_image_style",
        `/${selectedStyleId}?fields=*.*`,
        selectedStyleId !== null
    );

    useEffect(() => {
        if (successItem && dataItem) {
            console.log("Image style data:", dataItem);
            console.log("model  data:", dataItem?.models);

            // Set model options from dataItem.model
            if (dataItem.models && Array.isArray(dataItem.models)) {
                setModelOptions(dataItem.models);
            }
        }
    }, [successItem, dataItem]);

    const imageStyleFields = dataItem?.inputs || [
        {
            key: "object",
            label: "Object",
            type: "text",
        },
        {
            key: "object_photo",
            label: "Image",
            type: "image",
        },
    ];

    const handleSubmit = async (values) => {
        try {
            // Build user_inputs object from imageStyleInputs
            const user_inputs = {};
            imageStyleFields.forEach((field) => {
                if (imageStyleInputs[field.key] !== undefined) {
                    user_inputs[field.key] = imageStyleInputs[field.key];
                }
            });

            const payload = {
                user_input: user_inputs,
                id: selectedStyleId,
                model_name: values.model_name,
                aspect_ratio: values.aspect_ratio,
            };

            // Add optional fields only if they have values
            if (values.negative_space) {
                payload.negative_space = values.negative_space;
            }
            if (values.object_position) {
                payload.object_position = values.object_position;
            }

            console.log("Submission payload:", payload);
            console.log("User inputs:", user_inputs);
            mutationImageGen.mutate(payload);
            message.success("Form submitted successfully!");

            // TODO: Make API call here
            // await axios.post('/your-api-endpoint', payload);
        } catch (error) {
            console.error("Submission error:", error);
            message.error("Failed to submit form");
        }
    };

    const handleImageStyleInputChange = (key, value) => {
        setImageStyleInputs((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const renderImageStyleField = (field) => {
        switch (field.type) {
            case "text":
                return (
                    <Input
                        value={imageStyleInputs[field.key]}
                        onChange={(e) =>
                            handleImageStyleInputChange(
                                field.key,
                                e.target.value
                            )
                        }
                        placeholder={`Enter ${field.label}`}
                    />
                );
            case "image":
                return (
                    <PhotoButton
                        field={{
                            collection: "qdb_image_style",
                            field: field.key,
                        }}
                        width="150px"
                        height="150px"
                        objectFit="contain"
                        show_placeholder={true}
                        background="white"
                        enable_remove_bg={false}
                        onChange={(imageId) => {
                            // This gets called with just the ID
                            console.log("image input id", imageId);
                            handleImageStyleInputChange(field.key, imageId);
                        }}
                        onChangeFull={(photo_obj) => {
                            // This gets called with full object (optional)
                            console.log("image input full obj", photo_obj);
                        }}
                    />
                );
            default:
                return <Input />;
        }
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
    };

    return (
        <div style={{ maxWidth: 600, margin: "0 auto", padding: 24 }}>
            <h2>Image Generator</h2>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                    label="Image Style"
                    name="image_style"
                    rules={[
                        {
                            required: true,
                            message: "Please select image style",
                        },
                    ]}
                >
                    <RelationFieldStandard
                        field={{
                            foreign_key_table: "qdb_image_style",
                            foreign_key_column: "id",
                            template: "{{name}}",
                        }}
                        onChange={(val) => {
                            form.setFieldsValue({ image_style: val });
                            setSelectedStyleId(val);
                        }}
                        placeholder="Select image style"
                    />
                </Form.Item>

                {loadingItem && <p>Loading style data...</p>}

                {imageStyleFields.map((field) => (
                    <Form.Item key={field.key} label={field.label}>
                        {renderImageStyleField(field)}
                    </Form.Item>
                ))}

                <Form.Item
                    label="Model Name"
                    name="model_name"
                    rules={[
                        { required: true, message: "Please select model name" },
                    ]}
                >
                    <Select placeholder="Select model">
                        {modelOptions.map((modelItem) => (
                            <Option
                                key={modelItem.model}
                                value={modelItem.model}
                            >
                                {modelItem.model}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Aspect Ratio"
                    name="aspect_ratio"
                    rules={[
                        {
                            required: true,
                            message: "Please select aspect ratio",
                        },
                    ]}
                >
                    <Select placeholder="Select aspect ratio">
                        <Option value="1:1">1:1 (Square)</Option>
                        <Option value="16:9">16:9 (Landscape)</Option>
                        <Option value="9:16">9:16 (Portrait)</Option>
                        <Option value="4:3">4:3</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Negative Space (Optional)"
                    name="negative_space"
                >
                    <TextArea
                        rows={3}
                        placeholder="e.g., top left corner upto covering 2/3rd of canvas"
                    />
                </Form.Item>

                <Form.Item
                    label="Object Position (Optional)"
                    name="object_position"
                >
                    <Select placeholder="Select object position" allowClear>
                        <Option value="top-left">Top Left</Option>
                        <Option value="top-center">Top Center</Option>
                        <Option value="top-right">Top Right</Option>
                        <Option value="center-left">Center Left</Option>
                        <Option value="center">Center</Option>
                        <Option value="center-right">Center Right</Option>
                        <Option value="bottom-left">Bottom Left</Option>
                        <Option value="bottom-center">Bottom Center</Option>
                        <Option value="bottom-right">Bottom Right</Option>
                    </Select>
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={mutationImageGen.isLoading}
                    >
                        Generate Image
                    </Button>
                </Form.Item>
            </Form>

            <Modal
                title="Generated Image"
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={[
                    <Button key="close" onClick={handleModalClose}>
                        Close
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        href={getImageURL(generatedImageId)}
                        download
                        target="_blank"
                    >
                        Download
                    </Button>,
                ]}
                width={800}
                centered
            >
                {generatedImageId && (
                    <div
                        style={{
                            textAlign: "center",
                            border: "2px solid black",
                        }}
                    >
                        <img
                            src={getImageURL(generatedImageId)}
                            alt="Generated"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "70vh",
                                borderRadius: 8,
                            }}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
}
